import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { trackAdClick } from '../utils/adTracking';

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  position: relative;
`;


const HeroSection = styled.section`
  position: relative;
  min-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
  //background: url('/public/images/FB_Banner.jpg') center/cover no-repeat;
  background: url('/images/FB_Banner.jpg') center/cover no-repeat;
  color: ${({ theme }) => theme.colors.heroText};
  text-align: center;
  padding: 6rem 1.5rem 4rem;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: ${({ theme }) => theme.mode === 'dark'
      ? 'rgba(4, 2, 5, 0.1)'
      : 'rgba(255, 255, 255, 0.05)'};
    z-index: 0;
  }
`;

const NewsBadge = styled.div`
  position: absolute;
  top: 2rem;
  left: 2rem;
  z-index: 10;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    top: 1rem;
    left: 1rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    top: 0.5rem;
    left: 0.5rem;
    width: 120px;
    height: auto;
  }
`;

const NewsBadgeImage = styled.img`
  width: 160px;
  height: auto;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 140px;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 120px;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 840px;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const HeroTagline = styled.span`
  letter-spacing: 0.4em;
  text-transform: uppercase;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.highlight};
`;

const HeroTitle = styled.h1`
  font-size: clamp(2rem, 6vw, 3.2rem);
  line-height: 1.1;
  color: #ffffff;
`;

const HeroSubtitle = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #38bdf8;
`;

const HeroActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.button`
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.white};
  padding: 0.95rem 2.5rem;
  border-radius: 999px;
  font-weight: 600;
  letter-spacing: 0.05em;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 14px 24px rgba(0, 199, 168, 0.45);
  }
`;

const OutlineButton = styled.button`
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => (theme.mode === 'dark' ? theme.colors.black : theme.colors.text)};
  padding: 0.95rem 2.2rem;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  font-weight: 600;
  letter-spacing: 0.05em;
  transition: all 0.3s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.white};
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
    transform: translateY(-4px);
  }
`;

const Section = styled.section`
  position: relative;
  padding: 0 1.5rem;
  background: ${({ theme }) => theme.colors.background};
`;

const SectionInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  gap: 3rem;
`;

const SectionHeader = styled.div`
  max-width: 820px;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Eyebrow = styled.span`
  text-transform: uppercase;
  letter-spacing: 0.3em;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.highlight};
`;

const SectionTitle = styled.h2`
  font-size: clamp(1.5rem, 2.5vw, 2rem);
  color: ${({ theme }) => theme.colors.text};
`;

const SectionCopy = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const TwoColumn = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2.25rem;
  align-items: start;
`;

const GradientCard = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground};
  border-radius: 24px;
  padding: 2.25rem;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  box-shadow: ${({ theme }) => theme.colors.cardShadow};
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CardTitle = styled.h3`
  font-size: 1.35rem;
  color: ${({ theme }) => theme.colors.text};
`;

const CardBody = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.7;
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(0, 199, 168, 0.2);
  border-radius: 999px;
  padding: 0.4rem 1rem;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.accent};
`;

const HighlightPanel = styled.div`
  background: ${({ theme }) => theme.colors.panelBackground};
  border: 1px solid ${({ theme }) => theme.colors.panelBorder};
  border-radius: 28px;
  padding: 2.5rem;
  display: grid;
  gap: 1.5rem;
  box-shadow: ${({ theme }) => theme.colors.panelShadow};
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 1.15rem;
`;

const ListItem = styled.li`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  color: ${({ theme }) => theme.colors.textSecondary};

  &::before {
    content: '•';
    color: ${({ theme }) => theme.colors.accent};
    font-size: 1.3rem;
    line-height: 1;
    margin-top: 0.15rem;
  }
`;

const VideoPanel = styled.div`
  background: ${({ theme }) => theme.colors.panelBackground};
  border-radius: 24px;
  padding: 2.5rem;
  border: 1px solid ${({ theme }) => theme.colors.panelBorder};
  text-align: center;
  display: grid;
  gap: 1.25rem;
  box-shadow: ${({ theme }) => theme.colors.panelShadow};
`;

const VideoPlaceholder = styled.div`
  width: 100%;
  height: 260px;
  border-radius: 18px;
  border: 2px dashed ${({ theme }) => theme.colors.cardBorder};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: ${({ theme }) => theme.colors.cardBackground};
`;

const ResourceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.75rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const ResourceCard = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground};
  border-radius: 22px;
  padding: 2rem;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  display: grid;
  gap: 0.75rem;
  box-shadow: ${({ theme }) => theme.colors.cardShadow};
`;

const ResourceTitle = styled.h3`
  color: ${({ theme }) => theme.colors.highlight};
  font-size: 1.15rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
`;

const ContactCard = styled.div`
  background: ${({ theme }) => theme.colors.contactCardBackground};
  border: 1px solid ${({ theme }) => theme.colors.contactCardBorder};
  border-radius: 20px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  color: ${({ theme }) => theme.colors.text};
  box-shadow: ${({ theme }) => theme.colors.cardShadow};
`;

const ContactLabel = styled.span`
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.28em;
  color: ${({ theme }) => theme.colors.highlight};
`;

const ContactValue = styled.span`
  font-size: 1.2rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text};
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${({ theme }) => theme.colors.text};
  text-decoration: none;
  font-size: 0.95rem;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    opacity: 0.8;
    transform: translateX(4px);
  }
`;

const MeetingDetailsTitle = styled.h3`
  color: ${({ theme }) => theme.colors.text};
  font-size: 1.6rem;
  margin-bottom: 1.5rem;
`;

const ContactFormWrapper = styled.div`
  background: ${({ theme }) => theme.colors.panelBackground};
  border: 1px solid ${({ theme }) => theme.colors.panelBorder};
  border-radius: 24px;
  padding: 2.5rem;
  display: grid;
  gap: 1.5rem;
  box-shadow: ${({ theme }) => theme.colors.panelShadow};
`;

const ContactForm = styled.form`
  display: grid;
  gap: 1rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FormLabel = styled.label`
  color: ${({ theme }) => theme.colors.text};
  font-weight: 600;
`;

const FormInput = styled.input`
  padding: 0.75rem;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  font-size: 1rem;
  background: ${({ theme }) => theme.colors.inputBackground};
  color: ${({ theme }) => theme.colors.inputText};

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.accent};
  }
`;

const FormTextarea = styled.textarea`
  min-height: 140px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  padding: 0.75rem;
  font-size: 1rem;
  background: ${({ theme }) => theme.colors.inputBackground};
  color: ${({ theme }) => theme.colors.inputText};
  resize: vertical;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.accent};
  }
`;

const SubmitButton = styled.button`
  justify-self: flex-start;
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.white};
  padding: 0.9rem 2.4rem;
  border-radius: 999px;
  font-weight: 600;
  letter-spacing: 0.05em;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 24px rgba(0, 199, 168, 0.35);
  }
`;

const ThankYouMessage = styled.div`
  color: #d24545;
  font-weight: 700;
`;

const MapPlaceholder = styled.div`
  height: 280px;
  border-radius: 22px;
  border: 2px dashed ${({ theme }) => theme.colors.cardBorder};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: ${({ theme }) => theme.colors.cardBackground};
  text-align: center;
  padding: 1.5rem;
`;

const SecondaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.white};
  padding: 0.9rem 2rem;
  border-radius: 999px;
  font-weight: 600;
  letter-spacing: 0.05em;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 14px 26px rgba(0, 199, 168, 0.4);
  }
`;

const FeedsTabContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
  background: ${({ theme }) => theme.colors.panelBackground};
  border-radius: 12px;
  padding: 0.5rem;
  border: 1px solid ${({ theme }) => theme.colors.panelBorder};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const FeedsTab = styled.button<{ isActive: boolean }>`
  background: ${({ isActive, theme }) => 
    isActive ? theme.colors.accent : 'transparent'};
  color: ${({ isActive, theme }) => 
    isActive ? theme.colors.white : theme.colors.text};
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
  max-width: 200px;

  &:hover {
    background: ${({ isActive, theme }) => 
      isActive ? theme.colors.primary : theme.colors.cardBackground};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    max-width: none;
  }
`;

const FeedsContent = styled.div`
  background: ${({ theme }) => theme.colors.panelBackground};
  border-radius: 12px;
  padding: 2rem;
  border: 1px solid ${({ theme }) => theme.colors.panelBorder};
  min-height: 400px;
  box-shadow: ${({ theme }) => theme.colors.panelShadow};
`;

const FeedsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
`;

const FeedCard = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground};
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  transition: all 0.3s ease;
  box-shadow: ${({ theme }) => theme.colors.cardShadow};

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  }
`;

const FeedHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const PlatformIcon = styled.div<{ platform?: string }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ platform }) => platform === 'facebook' ? '1.4rem' : '1.2rem'};
  font-weight: ${({ platform }) => platform === 'facebook' ? '900' : '600'};
  background: ${({ platform }) => {
    if (platform === 'youtube') return '#FF0000';
    if (platform === 'facebook') return '#1877F2';
    if (platform === 'instagram') return '#E4405F';
    return '#10B981';
  }};
  color: #FFFFFF;
`;

const FeedPostInfo = styled.div`
  color: ${({ theme }) => theme.colors.text};
`;

const FeedPostTitle = styled.h3`
  font-size: 1.1rem;
  margin-bottom: 0.25rem;
  color: ${({ theme }) => theme.colors.text};
`;

const FeedPostDate = styled.span`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const FeedPostContent = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const FeedPostImage = styled.div`
  width: 100%;
  height: 150px;
  background: ${({ theme }) => theme.colors.cardBackground};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 1rem;
  border: 1px dashed ${({ theme }) => theme.colors.cardBorder};
`;

const FeedPostLink = styled.a`
  display: inline-block;
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.white};
  padding: 0.5rem 1rem;
  border-radius: 6px;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);
  }
`;

const FeedsComingSoon = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: 2rem;
`;

const FeedsComingSoonTitle = styled.h3`
  color: ${({ theme }) => theme.colors.accent};
  font-size: 1.3rem;
  margin-bottom: 1rem;
`;

const ScrollTargets = {
  welcome: 'welcome',
  about: 'about',
  offerings: 'offerings',
  meetings: 'meetings',
  resources: 'resources',
  videos: 'videos',
  feeds: 'feeds',
  contact: 'contact'
} as const;

const Home: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [activeFeedTab, setActiveFeedTab] = useState<'youtube' | 'facebook' | 'instagram'>('youtube');
  const [videos, setVideos] = useState<any[]>([]);
  const [videosLoading, setVideosLoading] = useState(true);

  const feedsPlatforms = {
    youtube: {
      name: 'YouTube',
      icon: '▶',
      url: 'https://www.youtube.com/@TheMensWell',
      posts: [
        {
          title: 'Welcome to The Men\'s Well',
          content: 'Introduction to our community and mission for men\'s emotional wellbeing.',
          date: 'Coming Soon',
          link: 'https://www.youtube.com/@TheMensWell'
        },
        {
          title: 'Emotional Management Techniques',
          content: 'Learn practical strategies for managing emotions during difficult times.',
          date: 'Coming Soon', 
          link: 'https://www.youtube.com/@TheMensWell'
        },
        {
          title: 'Community Stories',
          content: 'Hear from members about their journey with The Men\'s Well.',
          date: 'Coming Soon',
          link: 'https://www.youtube.com/@TheMensWell'
        }
      ]
    },
    facebook: {
      name: 'Facebook',
      icon: 'f',
      url: 'https://www.facebook.com/profile.php?id=61556669024858',
      posts: [
        {
          title: 'Community Updates',
          content: 'Stay connected with our latest news and meeting announcements.',
          date: 'Coming Soon',
          link: 'https://www.facebook.com/profile.php?id=61556669024858'
        },
        {
          title: 'Success Stories',
          content: 'Read inspiring stories from our community members.',
          date: 'Coming Soon',
          link: 'https://www.facebook.com/profile.php?id=61556669024858'
        },
        {
          title: 'Weekly Wisdom',
          content: 'Motivational posts and tips for emotional management.',
          date: 'Coming Soon',
          link: 'https://www.facebook.com/profile.php?id=61556669024858'
        }
      ]
    },
    instagram: {
      name: 'Instagram',
      icon: '📷',
      url: 'https://instagram.com/themenswell',
      posts: [
        {
          title: 'Daily Inspiration',
          content: 'Follow us for daily motivational quotes and wellness tips.',
          date: 'Coming Soon',
          link: 'https://instagram.com/themenswell'
        },
        {
          title: 'Behind the Scenes',
          content: 'Get a glimpse into our meetings and community activities.',
          date: 'Coming Soon',
          link: 'https://instagram.com/themenswell'
        },
        {
          title: 'Mindful Moments',
          content: 'Visual reminders for mindfulness and self-care practices.',
          date: 'Coming Soon',
          link: 'https://instagram.com/themenswell'
        }
      ]
    }
  };

  useEffect(() => {
    const state = location.state as { scrollTo?: string } | null;
    const hashTarget = location.hash?.replace('#', '');

    const target = state?.scrollTo || hashTarget;
    if (target) {
      setTimeout(() => {
        const el = document.getElementById(target);
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 0);

      if (state?.scrollTo) {
        navigate(location.pathname + (hashTarget ? `#${hashTarget}` : ''), {
          replace: true
        });
      }
    }
  }, [location, navigate]);

  // Facebook widget management when tab changes
  useEffect(() => {
    if (activeFeedTab === 'facebook') {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        if ((window as any).FB) {
          (window as any).FB.XFBML.parse(document.getElementById('facebook-feed-container'));
        }
      }, 100);
    } else {
      // Aggressively clear ALL Facebook elements when not on Facebook tab
      const allFbElements = document.querySelectorAll('.fb-page, .fb_iframe_widget, iframe[src*="facebook"], div[class*="fb-"], span[class*="fb-"]');
      allFbElements.forEach(el => {
        const parent = el.parentElement;
        if (parent && !parent.closest('#facebook-feed-container')) {
          el.remove();
        }
      });
      
      // Also clear the fb-root content since we're not on Facebook tab
      const fbRoot = document.getElementById('fb-root');
      if (fbRoot) {
        fbRoot.innerHTML = '';
      }
    }
  }, [activeFeedTab]);

  // Fetch videos from database
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setVideosLoading(true);
        const response = await fetch('/api/videos.php?limit=5');
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.videos) {
            setVideos(data.videos);
          }
        }
      } catch (error) {
        console.error('Error fetching videos:', error);
        // Keep empty array on error
        setVideos([]);
      } finally {
        setVideosLoading(false);
      }
    };

    fetchVideos();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // For localhost development - simulate success
    if (window.location.hostname === 'localhost') {
      console.log('Development mode - form data:', formData);
      setIsSubmitted(true);
      setFormData({ firstName: '', lastName: '', email: '', message: '' });
      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
      return;
    }

    // Production PHP submission
    try {
      const response = await fetch('/contact.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (result.success) {
        setIsSubmitted(true);
        setFormData({ firstName: '', lastName: '', email: '', message: '' });
        setTimeout(() => {
          setIsSubmitted(false);
        }, 5000);
      } else {
        alert('Error: ' + result.message);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Sorry, there was an error sending your message. Please try again.');
    }
  };

  return (
    <PageWrapper>

      <HeroSection id={ScrollTargets.welcome}>
        <NewsBadge 
          onClick={() => navigate('/news-events')}
          title="Click to see the latest Men's Well News & Events"
        >
          <NewsBadgeImage 
            src="/images/NewsBadgeChristmas.png" 
            alt="Latest Men's Well News & Events" 
          />
        </NewsBadge>
        
        <HeroContent>
          <HeroTagline>The Best Version Of You</HeroTagline>
          <HeroTitle>The Men's Well objective is to help men with emotional management, to exist harmoniously with self in adversity</HeroTitle>
          <HeroSubtitle>
            The Men's Well supports men across the northern suburbs of Wollongong with grounded
            emotional management practices, guided discussions, and purposeful community.
          </HeroSubtitle>
          <HeroActions>
            <PrimaryButton
              onClick={() =>
                document.getElementById(ScrollTargets.contact)?.scrollIntoView({ behavior: 'smooth' })
              }
            >
              Connect With Us
            </PrimaryButton>
            <OutlineButton
              onClick={() =>
                document.getElementById(ScrollTargets.offerings)?.scrollIntoView({
                  behavior: 'smooth'
                })
              }
            >
              Explore our support
            </OutlineButton>
          </HeroActions>
        </HeroContent>
      </HeroSection>

      <Section id={ScrollTargets.about} style={{
        marginTop: '-4rem',
        marginBottom: '4rem',
        position: 'relative',
        minHeight: '68vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'url("/men-talking.jpg") center 5%/cover no-repeat',
        color: '#ffffff',
        textAlign: 'center',
        padding: '10rem 1.5rem 4rem'
      }}>
        <div style={{
          content: '',
          position: 'absolute',
          inset: 0,
          background: 'rgba(4, 2, 5, 0.4)',
          zIndex: 0
        }} />
        <SectionInner style={{ position: 'relative', zIndex: 1 }}>
          <SectionHeader style={{ maxWidth: '840px', margin: '0 auto' }}>
            <Eyebrow style={{ color: '#38bdf8' }}>About The Men's Well</Eyebrow>
            <SectionTitle style={{ color: '#ffffff', fontSize: 'clamp(1.8rem, 4vw, 2.5rem)' }}>
              Located in Bulli at Northern Illawarra Uniting Church and supporting men since April 2024.
            </SectionTitle>
            <SectionCopy style={{ color: '#ffffff', fontSize: '1.1rem', lineHeight: '1.8' }}>
              The Men's Well (TMW) offers practices, conversations, and tools to help men manage
              emotions and reactions when life becomes challenging. Our aim is to build peaceful,
              manageable lives through self-awareness, resilience, and community.
            </SectionCopy>
            <SectionCopy style={{ color: '#ffffff', fontSize: '1.1rem', lineHeight: '1.8' }}>
              We provide in-person meeting groups, media support, and practical resources so you can
              become your own best friend even in the most difficult seasons.
            </SectionCopy>
          </SectionHeader>
        </SectionInner>
      </Section>

      <Section id={ScrollTargets.offerings} style={{ paddingTop: '2rem', paddingBottom: '4rem' }}>
        <SectionInner>
          <SectionHeader>
            <Eyebrow>Support Pathways</Eyebrow>
            <SectionTitle>Holistic care that keeps you grounded between gatherings.</SectionTitle>
            <SectionCopy>
              Each touchpoint blends guided reflection with easy-to-use tools. Here is how we help you
              stay steady when emotions rise.
            </SectionCopy>
          </SectionHeader>

          <TwoColumn>
            <GradientCard>
              <Pill>Facilitated groups</Pill>
              <CardTitle>Face-to-face meetings</CardTitle>
              <CardBody>
                Honest conversation and guided techniques to unpack stress, disappointment, and
                change—so you leave with clarity and practical steps to keep moving forward.
              </CardBody>
            </GradientCard>
            <GradientCard>
              <Pill>Media & tools</Pill>
              <CardTitle>Guides and exercises</CardTitle>
              <CardBody>
                Worksheets, mindfulness prompts, and resource lists that help you practise emotional
                regulation day-to-day, no matter what you are facing.
              </CardBody>
            </GradientCard>
            <GradientCard>
              <Pill>Community</Pill>
              <CardTitle>Accountable mateship</CardTitle>
              <CardBody>
                A supportive circle that champions personal growth, steady habits, and accountability—
                ensuring no one navigates adversity alone.
              </CardBody>
            </GradientCard>
          </TwoColumn>
        </SectionInner>
      </Section>

      <Section id={ScrollTargets.meetings} style={{
        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(59, 130, 246, 0.08))',
        marginTop: 0,
        paddingTop: '6rem',
        paddingBottom: '6rem'
      }}>
        <SectionInner>
          <SectionHeader>
            <Eyebrow>Gather Together</Eyebrow>
            <SectionTitle>We meet every 2nd & 4th Tuesday at Northern Illawarra Uniting Church.</SectionTitle>
          </SectionHeader>

          <TwoColumn>
            <div style={{ borderRadius: '22px', overflow: 'hidden', boxShadow: '0 30px 48px rgba(0, 0, 0, 0.25)' }}>
              <img 
                src="/2Men-standing.jpg" 
                alt="Two men from The Men's Well group standing together" 
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              <div style={{ 
                background: 'rgba(186, 194, 255, 0.28)', 
                borderRadius: '18px', 
                padding: '2.25rem',
                border: '1px solid rgba(4, 2, 5, 0.12)'
              }}>
                <MeetingDetailsTitle>
                  Here are the details:
                </MeetingDetailsTitle>
                <List>
                  <ListItem>
                    <strong>Where:</strong> Northern Illawarra Uniting Church
                  </ListItem>
                  <ListItem>
                    <strong>Address:</strong> 191 Princes Highway, Bulli NSW 2516
                  </ListItem>
                  <ListItem>
                    <strong>Time:</strong> 7:00pm – 8:30pm
                  </ListItem>
                  <ListItem>
                    <strong>When:</strong> 2nd & 4th Tuesday of every month
                  </ListItem>
                </List>
              </div>

              <div style={{
                background: 'rgba(255, 255, 255, 0.86)',
                borderRadius: '18px',
                padding: '1.5rem',
                textAlign: 'center',
                border: '1px solid rgba(4, 2, 5, 0.12)'
              }}>
                <video 
                  controls
                  style={{
                    width: '100%',
                    height: '200px',
                    borderRadius: '12px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    marginBottom: '1rem',
                    objectFit: 'cover'
                  }}
                  poster=""
                >
                  <source src="/video/Matt-J-commercial.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                <p style={{ color: 'rgba(4, 2, 5, 0.65)', fontSize: '1rem' }}>
                  Watch this introduction to understand what our meetings are about
                </p>
              </div>
            </div>
          </TwoColumn>

          <div style={{ marginTop: '4rem' }}>
            <div style={{
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '24px',
              padding: '2.5rem',
              border: '1px solid rgba(4, 2, 5, 0.12)',
              boxShadow: '0 30px 48px rgba(0, 0, 0, 0.15)'
            }}>
              <div style={{
                textAlign: 'center',
                marginBottom: '2rem'
              }}>
                <h3 style={{
                  fontSize: '1.6rem',
                  color: 'rgba(4, 2, 5, 0.9)',
                  marginBottom: '0.5rem',
                  fontWeight: '600'
                }}>
                  📅 TMW Calendar 2026
                </h3>
                <p style={{
                  color: 'rgba(4, 2, 5, 0.65)',
                  fontSize: '1.1rem',
                  lineHeight: '1.6'
                }}>
                  View our complete meeting schedule and important dates for 2026
                </p>
              </div>
              
              <div style={{
                borderRadius: '16px',
                overflow: 'hidden',
                border: '2px solid rgba(4, 2, 5, 0.08)',
                background: '#f8f9fa',
                height: '600px'
              }}>
                <iframe
                  src="/materials/TMW-Calendar-2026.pdf"
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none'
                  }}
                  title="TMW Calendar 2026"
                />
              </div>
              
              <div style={{
                marginTop: '1.5rem',
                textAlign: 'center'
              }}>
                <a
                  href="/materials/TMW-Calendar-2026.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    background: '#10B981',
                    color: 'white',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '999px',
                    textDecoration: 'none',
                    fontWeight: '600',
                    fontSize: '0.95rem',
                    transition: 'all 0.3s ease'
                  }}
                >
                  📄 Open Full PDF
                </a>
              </div>
            </div>
          </div>
        </SectionInner>
      </Section>

      <Section id={ScrollTargets.resources} style={{
        background: 'linear-gradient(135deg, rgba(139, 69, 19, 0.08), rgba(101, 163, 13, 0.1))',
        marginTop: 0,
        paddingTop: '6rem',
        paddingBottom: '6rem'
      }}>
        <SectionInner>
          <SectionHeader>
            <Eyebrow>Resources</Eyebrow>
            <SectionTitle>Education, tools, and media support ready for you.</SectionTitle>
            <SectionCopy>
              We are continually building out a library of materials to support you between meetings.
              Get in touch to access our resources while full downloads are prepared.
            </SectionCopy>
          </SectionHeader>

          <TwoColumn style={{ gap: '4rem' }}>
            <ResourceGrid>
              <ResourceCard>
                <Link 
                  to="/educational-materials"
                  style={{
                    textDecoration: 'none',
                    color: 'inherit',
                    cursor: 'pointer'
                  }}
                >
                  <ResourceTitle style={{
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.color = '#10B981';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.color = '';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                  >
                    Educational Materials →
                  </ResourceTitle>
                </Link>
                <SectionCopy>
                  Articles, guides, and educational content focused on emotional wellbeing and personal
                  development.
                </SectionCopy>
              </ResourceCard>
              <ResourceCard>
                <Link 
                  to="/wisdom-files"
                  style={{
                    textDecoration: 'none',
                    color: 'inherit',
                    cursor: 'pointer'
                  }}
                >
                  <ResourceTitle style={{
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.color = '#10B981';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.color = '';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                  >
                    TMW Wisdom Files →
                  </ResourceTitle>
                </Link>
                <SectionCopy>
                  Here are some slogans to help you align Honesty and Attitudes.
                </SectionCopy>
              </ResourceCard>
              <ResourceCard>
                <ResourceTitle>Audio Support</ResourceTitle>
                <SectionCopy>
                  Guided meditations and reflective practices you can use whenever you need to reset.
                </SectionCopy>
              </ResourceCard>
              <ResourceCard>
                <ResourceTitle>Worksheets & Tools</ResourceTitle>
                <SectionCopy>
                  Practical downloads to track progress, build habits, and remain accountable to your
                  goals.
                </SectionCopy>
              </ResourceCard>
              <ResourceCard>
                <ResourceTitle>Community Values</ResourceTitle>
                <SectionCopy>
                  Meeting guidelines, purpose, and the foundations that keep The Men's Well experience
                  safe and supportive.
                </SectionCopy>
              </ResourceCard>
              <ResourceCard>
                <ResourceTitle>Future Resources</ResourceTitle>
                <SectionCopy>
                  Additional resources and tools will be added here as we continue to expand our support
                  materials for the community.
                </SectionCopy>
              </ResourceCard>
            </ResourceGrid>

            <img 
              src="/MB-Believe.jpg" 
              alt="Men's wellbeing motivational image" 
              style={{ width: '85%', height: 'auto', display: 'block', margin: '0 0 0 100px', borderRadius: '22px', boxShadow: '0 30px 48px rgba(0, 0, 0, 0.25)' }}
            />
          </TwoColumn>
        </SectionInner>
      </Section>

      <Section id={ScrollTargets.videos} style={{ paddingTop: '4rem', paddingBottom: '4rem' }}>
        <SectionInner>
          <SectionHeader>
            <Eyebrow>Video Library</Eyebrow>
            <SectionTitle>Featured videos to support your wellbeing journey.</SectionTitle>
            <SectionCopy>
              Watch our featured content including emotional management strategies, community stories, 
              and guided practices. Access our complete video library for more resources.
            </SectionCopy>
          </SectionHeader>

          {/* Main Featured Video */}
          <VideoPanel>
            {videosLoading ? (
              <div style={{ 
                height: '320px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                background: 'rgba(4, 2, 5, 0.05)',
                borderRadius: '18px'
              }}>
                Loading featured video...
              </div>
            ) : (
              (() => {
                const mainVideo = videos.find(v => v.tier_order === 1);
                if (!mainVideo) {
                  return (
                    <div style={{ 
                      height: '320px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      background: 'rgba(4, 2, 5, 0.05)',
                      borderRadius: '18px',
                      color: 'rgba(4, 2, 5, 0.65)'
                    }}>
                      No featured video available
                    </div>
                  );
                }
                return (
                  <>
                    <div style={{ borderRadius: '18px', overflow: 'hidden', height: '320px' }}>
                      <iframe
                        width="100%"
                        height="100%"
                        src={`https://www.youtube.com/embed/${mainVideo.embedId}`}
                        title={mainVideo.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        style={{ border: 0 }}
                      ></iframe>
                    </div>
                    <p style={{ 
                      color: 'rgba(4, 2, 5, 0.65)', 
                      fontSize: '1.1rem', 
                      marginTop: '1rem',
                      textAlign: 'center',
                      fontWeight: '600'
                    }}>
                      Featured: {mainVideo.title}
                    </p>
                  </>
                );
              })()
            )}
          </VideoPanel>

          {/* 4 Thumbnail Videos */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
            gap: '1.5rem', 
            marginTop: '3rem' 
          }}>
            {videosLoading ? (
              // Show loading placeholders
              Array.from({ length: 4 }, (_, index) => (
                <div key={`loading-${index}`} style={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  border: '1px solid rgba(4, 2, 5, 0.12)',
                  height: '200px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'rgba(4, 2, 5, 0.65)'
                }}>
                  Loading video...
                </div>
              ))
            ) : (
              videos
                .filter(video => video.tier_order >= 2 && video.tier_order <= 5)
                .sort((a, b) => a.tier_order - b.tier_order)
                .map((video) => (
              <div key={video.id} style={{
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '16px',
                overflow: 'hidden',
                border: '1px solid rgba(4, 2, 5, 0.12)',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.15)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                  <iframe
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%'
                    }}
                    src={`https://www.youtube.com/embed/${video.embedId}`}
                    title={video.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                  <div style={{
                    position: 'absolute',
                    bottom: '8px',
                    right: '8px',
                    background: 'rgba(0, 0, 0, 0.8)',
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '0.8rem',
                    fontWeight: '600'
                  }}>
                    {video.duration}
                  </div>
                </div>
                <div style={{ padding: '1rem' }}>
                  <h4 style={{ 
                    fontSize: '1rem', 
                    marginBottom: '0.5rem',
                    color: 'rgba(4, 2, 5, 0.9)',
                    fontWeight: '600'
                  }}>
                    {video.title}
                  </h4>
                  <p style={{ 
                    fontSize: '0.9rem', 
                    color: 'rgba(4, 2, 5, 0.65)',
                    lineHeight: '1.4'
                  }}>
                    {video.description}
                  </p>
                </div>
              </div>
            ))
            )}
          </div>

          {/* View All Videos Button */}
          <div style={{ textAlign: 'center', marginTop: '3rem' }}>
            <Link 
              to="/videos"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.75rem',
                background: '#10B981',
                color: 'white',
                padding: '1rem 2rem',
                borderRadius: '999px',
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '1rem',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = '#059669';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(16, 185, 129, 0.3)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = '#10B981';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              View All Videos
              <span style={{ fontSize: '1.2rem' }}>→</span>
            </Link>
          </div>
        </SectionInner>
      </Section>

      <Section id={ScrollTargets.feeds} style={{
        background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.08), rgba(168, 85, 247, 0.1))',
        marginTop: 0,
        paddingTop: '6rem',
        paddingBottom: '6rem'
      }}>
        <SectionInner style={{ maxWidth: activeFeedTab === 'facebook' ? '1200px' : '1100px' }}>
          <SectionHeader>
            <Eyebrow>Social Media Feeds</Eyebrow>
            <SectionTitle>Stay connected with our community across all platforms.</SectionTitle>
            <SectionCopy>
              Follow our journey and join the conversation on YouTube, Facebook, and Instagram. 
              Get updates, inspiration, and connect with fellow community members.
            </SectionCopy>
          </SectionHeader>

          <FeedsTabContainer>
            {Object.entries(feedsPlatforms).map(([key, platform]) => (
              <FeedsTab
                key={key}
                isActive={activeFeedTab === key}
                onClick={() => setActiveFeedTab(key as keyof typeof feedsPlatforms)}
              >
                {platform.icon} {platform.name}
              </FeedsTab>
            ))}
          </FeedsTabContainer>

          <FeedsContent style={{ padding: activeFeedTab === 'facebook' ? 0 : '2rem' }}>
            {activeFeedTab === 'facebook' ? (
              <div 
                key="facebook-tab"
                id="facebook-feed-container"
                style={{ 
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '1rem'
                }}
              >
                <div 
                  key={`facebook-widget-${Date.now()}`}
                  className="fb-page" 
                  data-href="https://www.facebook.com/profile.php?id=61556669024858"
                  data-tabs="timeline"
                  data-width="500" 
                  data-height="700"
                  data-small-header="false"
                  data-adapt-container-width="false"
                  data-hide-cover="false"
                  data-show-facepile="true"
                />
              </div>
            ) : activeFeedTab === 'youtube' ? (
              <div 
                key="youtube-tab"
                id="youtube-feed-container"
                style={{ 
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '1rem'
                }}
              >
                <div style={{
                  background: '#f8f9fa',
                  borderRadius: '20px',
                  padding: '2rem',
                  textAlign: 'center',
                  maxWidth: '600px',
                  border: '1px solid #e9ecef',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ 
                    background: 'linear-gradient(135deg, #FF0000 0%, #CC0000 100%)',
                    borderRadius: '50%',
                    width: '80px',
                    height: '80px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1.5rem',
                    fontSize: '2rem',
                    color: 'white'
                  }}>
                    ▶
                  </div>
                  <h3 style={{ fontSize: '1.6rem', marginBottom: '1rem', color: '#333', fontWeight: 'bold' }}>
                    Watch Our 23 Videos
                  </h3>
                  <p style={{ marginBottom: '2rem', color: '#666', lineHeight: '1.5' }}>
                    Explore our complete collection of workshops, discussions, and resources about men's emotional wellbeing. 
                    Click below to visit our YouTube channel directly.
                  </p>
                  <a 
                    href="https://www.youtube.com/@TheMensWell"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-block',
                      background: 'linear-gradient(135deg, #FF0000 0%, #CC0000 100%)',
                      color: 'white',
                      padding: '12px 28px',
                      borderRadius: '25px',
                      textDecoration: 'none',
                      fontWeight: 'bold',
                      transition: 'all 0.3s ease',
                      fontSize: '0.95rem'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 0, 0, 0.4)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    Visit YouTube Channel
                  </a>
                </div>
              </div>
            ) : activeFeedTab === 'instagram' ? (
              <div 
                key="instagram-tab"
                id="instagram-feed-container"
                style={{ 
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '2rem'
                }}
              >
                <div style={{
                  background: '#f8f9fa',
                  borderRadius: '20px',
                  padding: '2rem',
                  textAlign: 'center',
                  maxWidth: '600px',
                  border: '1px solid #e9ecef',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ 
                    background: 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)',
                    borderRadius: '50%',
                    width: '80px',
                    height: '80px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1.5rem',
                    fontSize: '2rem'
                  }}>
                    📷
                  </div>
                  <h3 style={{ fontSize: '1.6rem', marginBottom: '1rem', color: '#333', fontWeight: 'bold' }}>
                    Instagram Content Coming Soon
                  </h3>
                  <p style={{ marginBottom: '2rem', color: '#666', lineHeight: '1.5' }}>
                    We're actively building our Instagram presence with behind-the-scenes content, 
                    community highlights, and wellness tips. Follow us to be the first to see our posts!
                  </p>
                  <a 
                    href="https://instagram.com/themenswell"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-block',
                      background: 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)',
                      color: 'white',
                      padding: '12px 28px',
                      borderRadius: '25px',
                      textDecoration: 'none',
                      fontWeight: 'bold',
                      transition: 'all 0.3s ease',
                      fontSize: '0.95rem'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(188, 24, 136, 0.4)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    Follow @themenswell
                  </a>
                </div>
              </div>
            ) : null}
          </FeedsContent>
        </SectionInner>
      </Section>

      <Section id={ScrollTargets.contact} style={{
        background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.08), rgba(219, 39, 119, 0.1))',
        marginTop: 0,
        paddingTop: '6rem',
        paddingBottom: '6rem'
      }}>
        <SectionInner>
          <SectionHeader>
            <Eyebrow>Contact</Eyebrow>
            <SectionTitle>Reach out and we will help you plan your first visit.</SectionTitle>
            <SectionCopy>
              Whether you want to attend the next gathering or simply ask questions, we are ready to
              chat. Use the form or contact details below and we will be in touch soon.
            </SectionCopy>
          </SectionHeader>

          <ContactGrid>
            <ContactCard>
              <ContactLabel>Call</ContactLabel>
              <ContactValue>0430 209 195</ContactValue>
            </ContactCard>
            <ContactCard>
              <ContactLabel>Email</ContactLabel>
              <ContactValue>themenswellgroup@gmail.com</ContactValue>
            </ContactCard>
            <ContactCard>
              <ContactLabel>Social</ContactLabel>
              <ContactValue>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <SocialLink 
                    href="https://www.facebook.com/profile.php?id=61556669024858"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#1877F2' }}>f</span>
                    Facebook
                  </SocialLink>
                  <SocialLink 
                    href="https://instagram.com/themenswell"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span style={{ fontSize: '1.2rem', color: '#E4405F' }}>📷</span>
                    @themenswell
                  </SocialLink>
                  <SocialLink 
                    href="https://www.youtube.com/@TheMensWell"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span style={{ fontSize: '1.2rem', color: '#FF0000' }}>▶</span>
                    @TheMensWell
                  </SocialLink>
                </div>
              </ContactValue>
            </ContactCard>
          </ContactGrid>

          <ContactFormWrapper>
            <ContactForm onSubmit={handleSubmit}>
              <FormRow>
                <FormField>
                  <FormLabel htmlFor="firstName">First Name</FormLabel>
                  <FormInput
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                  />
                </FormField>
                <FormField>
                  <FormLabel htmlFor="lastName">Last Name</FormLabel>
                  <FormInput
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                  />
                </FormField>
              </FormRow>
              <FormField>
                <FormLabel htmlFor="email">Email *</FormLabel>
                <FormInput
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </FormField>
              <FormField>
                <FormLabel htmlFor="message">Message</FormLabel>
                <FormTextarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Tell us how we can support you..."
                />
              </FormField>
              <SubmitButton type="submit">Send Message</SubmitButton>
              {isSubmitted && <ThankYouMessage>Thanks for contacting us!</ThankYouMessage>}
            </ContactForm>

            <div style={{ borderRadius: '22px', overflow: 'hidden', height: '280px' }}>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d13051.5!2d150.906!3d-34.342!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6b0d5b123456789a%3A0x123456789abcdef0!2s191%20Princes%20Hwy%2C%20Bulli%20NSW%202516%2C%20Australia!5e0!3m2!1sen!2sau!4v1640000000000!5m2!1sen!2sau"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Northern Illawarra Uniting Church Location"
              ></iframe>
            </div>

            <SecondaryButton to="/settings">View Settings & Preferences</SecondaryButton>
          </ContactFormWrapper>
        </SectionInner>
      </Section>
    </PageWrapper>
  );
};

export default Home;
